import { Injectable } from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as signalR from '@microsoft/signalr';
import { APP_CONFIG } from '@core/configs/app.config';
import { GLOBAL_DATA_KEY } from '@core/configs';
import { GlobalDataService } from './global-data.service';

/**
 * SignalR message structure
 */
interface SignalRMessage {
    target: string;
    arguments: any[];
}

/**
 * Service to manage SignalR connection for image processing hub
 * Manages connection lifecycle: connect -> join group -> process -> leave group -> disconnect
 */
@Injectable({ providedIn: 'root' })
export class SignalRConnectionService {
    private messageSubject = new Subject<SignalRMessage>();
    private connectionStatus = new BehaviorSubject<'connected' | 'disconnected' | 'error'>('disconnected');
    private hubConnection: signalR.HubConnection | null = null;
    // Serializes concurrent ensureConnected() calls so the socket is started once.
    private startPromise: Promise<void> | null = null;

    constructor(private globalDataService: GlobalDataService) {}

    /**
     * Subscribe to a SignalR method/target, optionally filtered by channelId.
     * With a persistent socket shared across batches, pass the batch's channelId
     * so each batch only sees its own events (the payload carries ChannelId).
     * @param methodName The SignalR method name to listen to
     * @param channelId  Optional batch channel to filter on
     */
    on<T = any>(methodName: string, channelId?: string): Observable<T> {
        return this.messageSubject.asObservable().pipe(
            filter(msg => msg.target === methodName),
            filter(msg => msg.arguments && msg.arguments.length > 0),
            filter(msg => msg.arguments[0]),
            filter(msg => !channelId || msg.arguments[0]?.channelId === channelId)
        ) as Observable<T>;
    }

    /**
     * Get connection status observable
     */
    getConnectionStatus(): Observable<'connected' | 'disconnected' | 'error'> {
        return this.connectionStatus.asObservable();
    }

    /**
     * Ensure the shared socket is started (idempotent). Reuses the existing
     * connection when already connected; does NOT join any group — grouping is
     * per-batch via joinGroup()/leaveGroup().
     */
    async ensureConnected(): Promise<void> {
        if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
            return;
        }
        if (this.startPromise) {
            return this.startPromise;
        }

        this.startPromise = (async () => {
            try {
                // Carry corpPrefix/officeCd as query params so server-side
                // connection lifecycle logs (connect/disconnect, join/leave)
                // have that context — they arrive before StartBatchProcessing and
                // are stable for the session, so the connection URL is the right
                // place to pass them.
                const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix) ?? '';
                const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd) ?? '';
                const hubUrl = `${APP_CONFIG.apiBaseUrl}/hubs/imageProcessing`
                    + `?corpprefix=${encodeURIComponent(corpPrefix)}&officecd=${encodeURIComponent(officeCd)}`;

                this.hubConnection = new signalR.HubConnectionBuilder()
                    .withUrl(hubUrl, {
                        skipNegotiation: true,
                        transport: signalR.HttpTransportType.WebSockets
                    })
                    .configureLogging(signalR.LogLevel.Information)
                    .build();

                this.setupEventHandlers();

                await this.hubConnection.start();

                // Guard: concurrent disconnect() may null out hubConnection during start()
                if (!this.hubConnection) {
                    throw new Error('Connection interrupted by concurrent disconnect');
                }

                this.connectionStatus.next('connected');
                console.log('[SignalR] socket connected');
            } catch (error) {
                this.connectionStatus.next('error');
                console.warn('[SignalR] socket start failed:', error);
                throw error;
            } finally {
                this.startPromise = null;
            }
        })();

        return this.startPromise;
    }

    /**
     * Join a per-batch processing group on the shared socket (does not start or
     * stop the socket). Group name is the batch's random channelId.
     */
    async joinGroup(channelId: string): Promise<void> {
        if (this.hubConnection?.state !== signalR.HubConnectionState.Connected) {
            throw new Error('SignalR not connected');
        }
        await this.hubConnection.invoke('JoinProcessingGroup', channelId);
    }

    /**
     * Leave a per-batch processing group without tearing down the socket.
     * Safe to call when already disconnected.
     */
    async leaveGroup(channelId: string): Promise<void> {
        if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.hubConnection.invoke('LeaveProcessingGroup', channelId);
            } catch {
                // Connection may be closing — leaving is best-effort.
            }
        }
    }

    /**
     * Stop the shared socket. Called by the dealer's idle-close, not per batch.
     * SignalR removes the connection from all its groups on stop.
     */
    async disconnect(): Promise<void> {
        const conn = this.hubConnection;
        if (!conn) {
            return;
        }

        // Detach the field BEFORE awaiting stop() so a concurrent ensureConnected()
        // never sees a still-"Connected" socket that is about to stop (which would
        // make a following joinGroup() throw). It will build a fresh connection instead.
        this.hubConnection = null;
        this.connectionStatus.next('disconnected');
        try {
            await conn.stop();
        } catch (error) {
            // Best-effort stop; the field is already cleared.
        }
    }

    /**
     * Setup SignalR event handlers
     */
    private setupEventHandlers(): void {
        if (!this.hubConnection) return;

        // Handle all incoming messages and route them through messageSubject
        const eventNames = ['ImageProcessing', 'ImageProcessed', 'BatchProcessingComplete', 'ProcessingError'];

        eventNames.forEach(eventName => {
            this.hubConnection!.on(eventName, (data: any) => {
                this.messageSubject.next({ target: eventName, arguments: [data] });
            });
        });

        this.hubConnection.onclose((error) => {
            this.connectionStatus.next('disconnected');
            if (error) {
                console.warn('[SignalR] socket closed with error:', error);
            } else {
                console.log('[SignalR] socket closed');
            }
        });
    }

    /**
     * Invoke a hub method over the existing WebSocket connection
     */
    async invoke(methodName: string, ...args: any[]): Promise<void> {
        if (this.hubConnection?.state !== signalR.HubConnectionState.Connected) {
            throw new Error('SignalR not connected');
        }
        await this.hubConnection.invoke(methodName, ...args);
    }

    /**
     * Check if currently connected
     */
    isConnected(): boolean {
        return this.hubConnection?.state === signalR.HubConnectionState.Connected;
    }

    /**
     * Emit a message (for testing purposes only)
     */
    emit(target: string, ...args: any[]): void {
        this.messageSubject.next({ target, arguments: args });
    }
}
